/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect } from "react";
import { SidebarAdmin, SubscriptionWarning } from "../components";
import AppHeader from "../components/Shell/AppHeader";
import { sideBarData, sideBarMemberData } from "../utils";
import { filterStaffSidebarByCapabilities } from "../utils/investorPortalRouteCapabilities";
import { filterStaffSidebarByRoleModules, normalizeStaffProfilePayload } from "../utils/staffRoleModules";
import { useCompanyCapabilities } from "../hooks/useCompanyCapabilities";
import { useDispatch } from "react-redux";
import { setUserData } from "../store/auth/index.js";
import { FiGift } from "react-icons/fi";
import { useUser, useUserType } from "../hooks";
import { useNavigate, useLocation } from "react-router-dom";
import SubscriptionBlockedPage from "../components/Subscription/SubscriptionBlockedPage";
import { useSubscription } from "../hooks/useSubscription";
import { useGetBirthdaysQuery } from "../store/admin/automatedBirthdaySlice";
import BirthdayCelebration from "../components/Common/BirthdayCelebration";
import { OnboardingTourHost } from "../components/Common/OnboardingTour";
import { useLazyGetCurrentUserProfileQuery } from "../store/auth/authApi";
import SupportSessionBanner from "../components/Common/SupportSessionBanner";

export default function AdminLayout({ children }) {
  const [hideSider, setHideSider] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useSubscription();

  const userType = useUserType();
  const { capabilities } = useCompanyCapabilities();
  const user = useUser();
  const [refreshProfile] = useLazyGetCurrentUserProfileQuery();

  useEffect(() => {
    if (userType?.toLowerCase().includes("member")) return undefined;

    const syncStaffProfile = () => {
      refreshProfile()
        .unwrap()
        .then((res) => {
          const profile = normalizeStaffProfilePayload(res?.data || res);
          if (profile?.staff_id || profile?.role) {
            dispatch(setUserData(profile));
          }
        })
        .catch(() => {});
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") syncStaffProfile();
    };

    // Pick up Manager module assignment changes without a full re-login.
    syncStaffProfile();
    window.addEventListener("focus", syncStaffProfile);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", syncStaffProfile);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userType]);

  const staffSidebar = useMemo(() => {
    const byCap = filterStaffSidebarByCapabilities(sideBarData, capabilities);
    return filterStaffSidebarByRoleModules(byCap, user?.role);
  }, [capabilities, user?.role]);
  
  // Determine guard from user type
  const guard = userType?.toLowerCase().includes("member") ? "member" : "staff";
  const menuItems = guard === "member" ? sideBarMemberData : staffSidebar;
  const isAdmin = guard === "staff";

  // Check for birthdays today (only for admins)
  const { data: birthdayData } = useGetBirthdaysQuery(1, { skip: !isAdmin || !isActive });
  const todayBirthdays = birthdayData?.automated_birthdays?.data || birthdayData?.automated_birthdays || [];
  const hasBirthdaysToday = isAdmin && todayBirthdays.length > 0;
  
  const notificationsBasePath =
    guard === "member" ? "/member/notifications" : "/super_admin/notifications";

  // An inactive plan replaces the whole app: the menu and header cannot be used, so they are not shown.
  // (Settings pages were exempt before, but their data calls are blocked too, so they were just empty.)
  if (!isActive) return <SubscriptionBlockedPage />;

  return (
    <div className="w-screen">
      <BirthdayCelebration />
      {/* <!-- ===== Page Wrapper Start ===== --> */}
      <div className="flex h-screen w-full overflow-hidden">
        {/* <!-- ===== Sidebar Start ===== --> */}
        <div className="w-fit">
          <SidebarAdmin
            hideSider={hideSider}
            setHideSider={setHideSider}
            sideBarData={menuItems}
          />
        </div>
        {/* <!-- ===== Sidebar End ===== --> */}

        {/* <!-- ===== Content Area Start ===== --> */}
        <div
          className=" min-[1200px]:ml-[280px] relative flex flex-1 w-full flex-col overflow-y-auto overflow-x-hidden bg-[#F8F8F8] "
        >
          <SupportSessionBanner />
          {/* <!-- ===== Header Start ===== --> */}
          <AppHeader
            guard={guard}
            items={menuItems}
            user={user}
            onMenuClick={() => setHideSider(!hideSider)}
            notificationsPath={notificationsBasePath}
            notificationsEnabled={isActive}
          />
          {/* <NavbarComp setHideSider={setHideSider} hideSider={hideSider} /> */}
          {/* <!-- ===== Header End ===== --> */}

          {/* <!-- ===== Main Content Start ===== --> */}
          <main className="min-h-screen flex w-full">
            <div className="mx-auto max-w-screen-2xl w-full p-4 md:p-6 2xl:p-10">
              {hasBirthdaysToday && location.pathname !== "/super_admin/automated_birthday" && (
                <div 
                  className="mb-4 p-3 rounded-4 d-flex align-items-center justify-content-between shadow-sm border-0"
                  style={{ 
                    background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)",
                    color: "white",
                    borderLeft: "5px solid #FFD700"
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                      style={{ width: '45px', height: '45px', color: 'var(--color-primary)' }}
                    >
                      <FiGift size={22} aria-hidden="true" />
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">Happy Birthday!</h6>
                      <p className="mb-0 small opacity-90">
                        <b>{todayBirthdays.length}</b> {todayBirthdays.length === 1 ? "person is" : "people are"} celebrating today. Let's make it special!
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate("/super_admin/automated_birthday")}
                    className="btn btn-sm btn-light text-primary1 fw-bold px-4 rounded-pill hover:scale-105 transition-all shadow-sm"
                  >
                    Send Wishes
                  </button>
                </div>
              )}
              <SubscriptionWarning />
              {children}
            </div>
          </main>
          {/* <!-- ===== Main Content End ===== --> */}
        </div>
        {/* <!-- ===== Content Area End ===== --> */}
      </div>
      {/* <!-- ===== Page Wrapper End ===== --> */}
      <OnboardingTourHost role="staff" />
    </div>
    // <div>
    //   {/* <SidebarAdmin
    //     hideSider={hideSider}
    //     setHideSider={setHideSider}
    //     sideBarData={sideBarData}
    //   /> */}

    //   <main id="main" className="">
    //     <div className="w-100  mb-3">
    //       {/* <NavbarComp setHideSider={setHideSider} hideSider={hideSider} /> */}
    //     </div>

    //     <div className=" w-100  maincontent mx-auto  mb-5">{children}</div>
    //   </main>
    // </div>
  );
}

// const SideBarStyle = styled.div`
//   .main {
//     overflow: hidden;
//     background: #f1f1f1 !important;
//     min-height: 92vh;
//     // margin-left: 295px;
//   }
//   #main {
//     /* margin-top: 60px; */
//     /* padding: 0px 30px; */
//     transition: all 0.3s;
//     background: #f1f1f1 !important;
//     min-height: 100vh;
//     overflow: hidden;
//   }
//   @media (max-width: 1199px) {
//     #main {
//       margin-left: 0;
//     }
//     .maincontent {
//       padding: 20px;
//     }
//   }
//   @media (max-width: 480px) {
//     .maincontent {
//       padding: 5px;
//     }
//   }
// `;
